import { useMemo } from 'react'
import { SpendSummary, SpendWindow } from '../lib/api'
import { ActivityMetrics } from '../hooks/useActivityCost'

// Personas that still route through Claude OAuth in non-AU mode.
// In AU mode (residency=au), all 13 personas route via LiteLLM and this list is unused.
const OAUTH_PERSONAS = new Set(['architect', 'prism', 'gatekeeper', 'terraformer', 'demiurge'])

interface ProjectSpendCardProps {
  residency: 'au' | 'none'
  activity: ActivityMetrics                  // from useActivityCost — loop counts only (no invented $)
  summary: SpendSummary | null
  loading: boolean
  error: string | null
  window: SpendWindow
  setWindow: (w: SpendWindow) => void
  reload: (refresh: boolean) => void
}

function formatMoney(n: number): string {
  if (n < 0.01 && n > 0) return `$${n.toFixed(4)}`
  return `$${n.toFixed(2)}`
}

const WINDOW_LABELS: Record<SpendWindow, string> = {
  '24h': 'Last 24h',
  '7d': 'Last 7d',
  '30d': 'Last 30d',
  total: 'Total',
  since_start: 'Since project start',
}

// How the backend found the window's start (SpendSummary.start_source).
const START_SOURCE_NOTES: Record<string, string> = {
  window: 'Summed',
  request: 'Summed',
  run_chain_start: 'Since the run started: summed',
  current_run_start: "Since the current run's start (a continued run's earlier spend is not included): summed",
  unknown_run_start: 'Run start unknown, so all time: summed',
}

export function ProjectSpendCard({ residency, activity, summary, loading, error, window, setWindow, reload }: ProjectSpendCardProps) {
  // OAuth activity: how many loops the 5 Anthropic personas ran in non-AU mode.
  // We report loop counts (real, from activity.log) — NOT an invented dollar figure,
  // since Claude OAuth runs on a flat-rate Max plan and per-token cost isn't observable here.
  // In AU mode this is always zero — all personas route via LiteLLM.
  const oauthActivity = useMemo(() => {
    if (residency === 'au') return { loops: 0 }
    const personas = activity.byPersona.filter(p => OAUTH_PERSONAS.has(p.persona))
    return { loops: personas.reduce((s, p) => s + p.loops, 0) }
  }, [activity, residency])

  if (loading && !summary) {
    return <div className="px-4 py-3 text-sm text-gray-500">Loading spend…</div>
  }
  if (error) {
    return <div className="px-4 py-3 text-sm text-red-400">Spend unavailable: {error}</div>
  }
  if (!summary) return null

  const showCash = summary.cash.spend > 0
  const showOauth = residency !== 'au' && oauthActivity.loops > 0

  return (
    <div className="px-4 py-3 space-y-3">
      {/* Window selector + refresh */}
      <div className="flex items-center gap-2 text-xs">
        <span className="text-gray-500 uppercase tracking-wider">Window:</span>
        <div className="flex rounded-lg overflow-hidden border border-cosmic-border">
          {(['24h', '7d', '30d', 'total', 'since_start'] as SpendWindow[]).map(w => (
            <button
              key={w}
              onClick={() => setWindow(w)}
              className={`px-2 py-1 ${window === w ? 'bg-cosmic-nebula-light text-white' : 'text-gray-400 hover:bg-cosmic-abyss/30'}`}
            >
              {WINDOW_LABELS[w]}
            </button>
          ))}
        </div>
        <button
          onClick={() => reload(true)}
          disabled={loading}
          className="ml-auto px-2 py-1 text-cosmic-nebula-light hover:text-white disabled:opacity-50"
          title="Re-fetch from LiteLLM (bypasses 5-min cache)"
        >
          {loading ? '...' : 'Refresh'}
        </button>
      </div>

      {/* Sponsorship credit */}
      <div>
        <div
          className="flex justify-between font-medium"
          title="Computed from published model prices. Actual cash outflow is $0 — covered by Microsoft Azure Sponsorship credits on the Sideway subscription."
        >
          <span>Sponsorship credit (Azure)</span>
          <span>{formatMoney(summary.sponsorship_credit.spend)} <span className="text-gray-500">({summary.sponsorship_credit.request_count} req)</span></span>
        </div>
        {summary.by_model.filter(m => m.vendor === 'sponsorship').map(m => (
          <div key={m.model} className="flex justify-between pl-4 text-xs text-gray-400">
            <span>↳ {m.model}</span>
            <span>{formatMoney(m.spend)} <span className="text-gray-600">({m.request_count} req)</span></span>
          </div>
        ))}
      </div>

      {/* Cash spend (hidden if zero for this window) */}
      {showCash && (
        <div>
          <div
            className="flex justify-between font-medium"
            title="Real billed amount against Sideway accounts, estimated from LiteLLM token counts × catalog prices. Reconcile monthly against vendor dashboards — drift up to ~5-10% is normal."
          >
            <span>Cash spend</span>
            <span>{formatMoney(summary.cash.spend)} <span className="text-gray-500">({summary.cash.request_count} req)</span></span>
          </div>
          {summary.by_model.filter(m => m.vendor === 'cash').map(m => (
            <div key={m.model} className="flex justify-between pl-4 text-xs text-gray-400">
              <span>↳ {m.model}</span>
              <span>{formatMoney(m.spend)} <span className="text-gray-600">({m.request_count} req)</span></span>
            </div>
          ))}
        </div>
      )}

      {/* Anthropic OAuth (loop activity — only in non-AU mode; flat-rate, not per-call billed) */}
      {showOauth && (
        <div>
          <div
            className="flex justify-between font-medium text-gray-300"
            title="Claude OAuth runs on a flat-rate Max plan ($340/mo) through 2026-06-15, after which a separate $200/mo SDK credit pool kicks in for headless usage. Per-call dollar cost is NOT observable here, so we report loop activity only — not an invented figure. Check console.anthropic.com for authoritative spend."
          >
            <span>Anthropic OAuth (flat-rate)</span>
            <span className="text-gray-500">{oauthActivity.loops} loops</span>
          </div>
        </div>
      )}

      {summary.complete === false && (
        <div className="text-xs text-amber-400">
          Partial: LiteLLM holds more rows for this window than one fetch reads, so this total is a lower bound.
        </div>
      )}

      {summary.range_start && summary.range_end && (
        <div className="text-xs text-gray-500" title={summary.tag ? `Matched on ${summary.tag}` : undefined}>
          {START_SOURCE_NOTES[summary.start_source ?? ''] ?? 'Summed'} from {new Date(summary.range_start).toLocaleString()} to{' '}
          {new Date(summary.range_end).toLocaleString()}
        </div>
      )}

      <div className="text-xs text-gray-600">
        Updated {new Date(summary.last_updated).toLocaleTimeString()} · Server-side cache: 5 min · Auto-refresh: 15 min
      </div>
    </div>
  )
}
